import { lazy, Suspense, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useIdleTimer } from './hooks/useIdleTimer';
import { IdleWarningModal } from './components/IdleWarningModal';
import { useConfig } from './contexts/ConfigContext';
import { ProtectedRoute } from './components/ProtectedRoute';

// Eager: pages on the critical first-paint path (landing / auth)
import LandingPage from './pages/LandingPage';
import LoginPage   from './pages/LoginPage';
import SignupPage  from './pages/SignupPage';

// Lazy: everything else is code-split so a student on an old Chromebook never
// downloads the admin/parent portals (or KaTeX/quiz code before entering chat).
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const EulaPage        = lazy(() => import('./pages/EulaPage'));

const StudentLayout    = lazy(() => import('./pages/student/StudentLayout'));
const StudentDashboard = lazy(() => import('./pages/student/StudentDashboard'));
const StudentChat      = lazy(() => import('./pages/student/StudentChat'));
const StudentStudio    = lazy(() => import('./pages/student/StudentStudio'));
const ProjectEditor    = lazy(() => import('./pages/student/ProjectEditor'));
const StudentBridge    = lazy(() => import('./pages/student/StudentBridge'));
const StudentProgress  = lazy(() => import('./pages/student/StudentProgress'));
const StudentNotes     = lazy(() => import('./pages/student/StudentNotes'));
const StudentSettings  = lazy(() => import('./pages/student/StudentSettings'));
const StudentSchedule  = lazy(() => import('./pages/student/StudentSchedule'));
const StudentPractice  = lazy(() => import('./pages/student/StudentPractice'));
const StudentProfile   = lazy(() => import('./pages/student/StudentProfile'));

const ParentLayout      = lazy(() => import('./pages/parent/ParentLayout'));
const ParentDashboard   = lazy(() => import('./pages/parent/ParentDashboard'));
const MyChildren        = lazy(() => import('./pages/parent/MyChildren'));
const ChildSessions     = lazy(() => import('./pages/parent/ChildSessions'));
const ParentalControls  = lazy(() => import('./pages/parent/ParentalControls'));
const ChildPortfolio    = lazy(() => import('./pages/parent/ChildPortfolio'));
const BridgeEnroll      = lazy(() => import('./pages/parent/BridgeEnroll'));
const BridgeProgress    = lazy(() => import('./pages/parent/BridgeProgress'));
const ParentReports     = lazy(() => import('./pages/parent/ParentReports'));
const ParentSettings    = lazy(() => import('./pages/parent/ParentSettings'));
const DataPrivacyPage   = lazy(() => import('./pages/parent/DataPrivacyPage'));

const AdminLayout     = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard  = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers      = lazy(() => import('./pages/admin/AdminUsers'));
const AdminErrors     = lazy(() => import('./pages/admin/AdminErrors'));
const AdminAnalytics  = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminModeration = lazy(() => import('./pages/admin/AdminModeration'));
const AdminSettings   = lazy(() => import('./pages/admin/AdminSettings'));
const AdminAuditLog   = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminFeedback   = lazy(() => import('./pages/admin/AdminFeedback'));

const OnboardingPage       = lazy(() => import('./pages/OnboardingPage'));
const TourPage              = lazy(() => import('./pages/TourPage'));
const PrivacyPolicyPage    = lazy(() => import('./pages/PrivacyPolicyPage'));
const FaqPage              = lazy(() => import('./pages/FaqPage'));
const ForgotPasswordPage   = lazy(() => import('./pages/ForgotPasswordPage'));
const PaymentCancelledPage = lazy(() => import('./pages/PaymentCancelledPage'));
const BillingPage          = lazy(() => import('./pages/BillingPage'));
const MaintenancePage      = lazy(() => import('./pages/MaintenancePage'));

function RouteFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #E2E8F0', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function RootRedirect() {
  const { user, groups, isLoading, userAttributes } = useAuth();
  const { features, isLoaded } = useConfig();
  if (isLoading || !isLoaded) return null;
  if (features.maintenanceMode && !groups.includes('admin')) return <MaintenancePage />;
  if (!user) return <LandingPage />;
  // Admin bypasses EULA — it's a student/parent agreement, not platform staff
  if (groups.includes('admin'))  return <Navigate to="/admin"  replace />;
  if (!userAttributes['custom:agreed_eula']) return <Navigate to="/eula" replace />;
  if (groups.includes('parent')) return <Navigate to="/parent" replace />;
  if (groups.includes('student')) {
    if (!userAttributes['custom:onboarding_done']) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/student" replace />;
  }
  return <Navigate to="/pending" replace />;
}

function PendingPage() {
  const { signOut } = useAuth();
  return (
    <div style={{ minHeight: '100vh', background: '#F4F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', padding: '24px', textAlign: 'center' }}>
      <div style={{ fontSize: '40px' }}>⏳</div>
      <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#1E1B4B' }}>Account pending activation</h1>
      <p style={{ fontSize: '14px', color: '#64748B', maxWidth: '380px', lineHeight: 1.6 }}>
        Your account was created successfully. An administrator needs to assign you to the correct group (student or parent) before you can access the platform. This typically takes a few minutes during beta.
      </p>
      <p style={{ fontSize: '13px', color: '#94A3B8' }}>Contact <a href="mailto:Michael@h3protech.com" style={{ color: '#4F46E5' }}>Michael@h3protech.com</a> if this takes more than 24 hours.</p>
      <button onClick={signOut} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', color: '#64748B', cursor: 'pointer' }}>
        Sign out
      </button>
    </div>
  );
}

function AnnouncementBanner() {
  const { announcement } = useConfig();
  if (!announcement.enabled || !announcement.message) return null;
  const bg   = announcement.type === 'warning' ? '#FFFBEB' : announcement.type === 'success' ? '#F0FDF4' : '#EFF6FF';
  const color = announcement.type === 'warning' ? '#92400E' : announcement.type === 'success' ? '#166534' : '#1E40AF';
  const border = announcement.type === 'warning' ? '#FCD34D' : announcement.type === 'success' ? '#BBF7D0' : '#BFDBFE';
  return (
    <div style={{ background: bg, color, borderBottom: `1px solid ${border}`, padding: '10px 20px', fontSize: 13, fontWeight: 500, textAlign: 'center' }}>
      {announcement.message}
    </div>
  );
}

const IDLE_MS    = 30 * 60 * 1000; // 30 minutes
const WARNING_MS = 60 * 1000;      // warn 60s before logout

function IdleGuard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);

  const handleLogout = async () => {
    setShowWarning(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  const { resetTimer } = useIdleTimer({
    idleMs:    IDLE_MS,
    warningMs: WARNING_MS,
    enabled:   !!user,
    onWarn:    () => setShowWarning(true),
    onLogout:  handleLogout,
  });

  if (!showWarning) return null;

  return (
    <IdleWarningModal
      countdownSeconds={WARNING_MS / 1000}
      onStay={() => { setShowWarning(false); resetTimer(); }}
      onLogout={handleLogout}
    />
  );
}

export default function App() {
  return (
    <>
      <AnnouncementBanner />
      <IdleGuard />
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public */}
      <Route path="/login"   element={<LoginPage />} />
      <Route path="/signup"  element={<SignupPage />} />
      <Route path="/verify"  element={<VerifyEmailPage />} />
      <Route path="/eula"        element={<EulaPage />} />
      <Route path="/pending"     element={<PendingPage />} />
      <Route path="/onboarding"  element={<OnboardingPage />} />
      <Route path="/tour"        element={<TourPage />} />
      <Route path="/privacy"     element={<PrivacyPolicyPage />} />
      <Route path="/faq"              element={<FaqPage />} />
      <Route path="/forgot-password"   element={<ForgotPasswordPage />} />
      <Route path="/billing/cancelled" element={<PaymentCancelledPage />} />

      {/* Student portal */}
      <Route path="/student" element={<ProtectedRoute roles={['student']}><StudentLayout /></ProtectedRoute>}>
        <Route index element={<StudentDashboard />} />
        <Route path="chat"              element={<StudentChat />} />
        <Route path="schedule"          element={<StudentSchedule />} />
        <Route path="practice"          element={<StudentPractice />} />
        <Route path="profile"           element={<StudentProfile />} />
        <Route path="studio"           element={<StudentStudio />} />
        <Route path="studio/:projectId" element={<ProjectEditor />} />
        <Route path="bridge"    element={<StudentBridge />} />
        <Route path="notes"     element={<StudentNotes />} />
        <Route path="progress"  element={<StudentProgress />} />
        <Route path="billing"   element={<BillingPage returnPath="/student" />} />
        <Route path="settings"  element={<StudentSettings />} />
      </Route>

      {/* Parent portal */}
      <Route path="/parent" element={<ProtectedRoute roles={['parent']}><ParentLayout /></ProtectedRoute>}>
        <Route index element={<ParentDashboard />} />
        <Route path="children" element={<MyChildren />} />
        <Route path="children/:childId" element={<ChildSessions />} />
        <Route path="children/:childId/portfolio" element={<ChildPortfolio />} />
        <Route path="bridge"             element={<BridgeEnroll />} />
        <Route path="bridge/progress"    element={<BridgeProgress />} />
        <Route path="billing"  element={<BillingPage returnPath="/parent" />} />
        <Route path="controls" element={<ParentalControls />} />
        <Route path="reports"       element={<ParentReports />} />
        <Route path="settings"      element={<ParentSettings />} />
        <Route path="data-privacy"  element={<DataPrivacyPage />} />
      </Route>

      {/* Admin portal */}
      <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users"      element={<AdminUsers />} />
        <Route path="errors"     element={<AdminErrors />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="analytics"  element={<AdminAnalytics />} />
        <Route path="settings"   element={<AdminSettings />} />
        <Route path="audit-logs" element={<AdminAuditLog />} />
        <Route path="feedback"   element={<AdminFeedback />} />
      </Route>

      {/* Root */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

